import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'shortlystat_analyses';

export const useAnalysisInbox = () => {
  const [analyses, setAnalyses] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);

  // Загружаем анализы из localStorage при инициализации
  useEffect(() => {
    const loadAnalyses = () => {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setAnalyses(parsed);
        } catch (error) {
          console.error('Ошибка при загрузке анализов из localStorage:', error);
          setAnalyses([]);
        }
      }
    };

    // Загружаем при инициализации
    loadAnalyses();

    // Слушаем изменения localStorage
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        loadAnalyses();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Также слушаем кастомное событие для изменений в том же окне
    const handleCustomStorageChange = () => {
      loadAnalyses();
    };
    
    window.addEventListener('localStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, []);

  // Сохраняем анализы в localStorage при изменении
  const saveToStorage = useCallback((newAnalyses) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newAnalyses));
      setAnalyses(newAnalyses);
      
      // Отправляем кастомное событие для синхронизации в том же окне
      window.dispatchEvent(new CustomEvent('localStorageChange'));
    } catch (error) {
      console.error('Ошибка при сохранении анализов в localStorage:', error);
    }
  }, []);

  // Добавляем новый анализ
  const addAnalysis = useCallback((analysisData) => {
    const newAnalysis = {
      id: Date.now().toString(), // Простой ID на основе времени
      taskId: analysisData.taskId,
      widgetName: analysisData.widgetName,
      analysisType: analysisData.analysisType || 'insights',
      status: 'pending',
      isRead: false,
      createdAt: new Date().toISOString(),
      completedAt: null,
      result: null,
      error: null,
      meta: analysisData.meta || {}
    };

    const updatedAnalyses = [newAnalysis, ...analyses];
    saveToStorage(updatedAnalyses);
    return newAnalysis.id;
  }, [analyses, saveToStorage]);

  // Обновляем статусы pending/processing анализов
  const updatePendingAnalyses = useCallback(async () => {
    const pendingAnalyses = analyses.filter(a => 
      a.status === 'pending' || a.status === 'processing'
    );

    if (pendingAnalyses.length === 0) {
      return; // Нет pending анализов
    }

    setIsUpdating(true);

    try {
      const updatePromises = pendingAnalyses.map(async (analysis) => {
        try {
          const response = await fetch(`/api/analysis/task/${analysis.taskId}`);
          if (response.ok) {
            const result = await response.json();
            return {
              id: analysis.id,
              status: result.status,
              result: result.data || null,
              error: result.error || null,
              completedAt: result.completedAt || null
            };
          }
        } catch (error) {
          console.error(`Ошибка при обновлении анализа ${analysis.id}:`, error);
        }
        return null;
      });

      const updates = await Promise.all(updatePromises);
      const validUpdates = updates.filter(update => update !== null);

      if (validUpdates.length > 0) {
        const updatedAnalyses = analyses.map(analysis => {
          const update = validUpdates.find(u => u.id === analysis.id);
          if (update) {
            return {
              ...analysis,
              status: update.status,
              result: update.result,
              error: update.error,
              completedAt: update.completedAt
            };
          }
          return analysis;
        });

        saveToStorage(updatedAnalyses);
      }
    } catch (error) {
      console.error('Ошибка при обновлении анализов:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [analyses, saveToStorage]);

  // Помечаем анализ как прочитанный
  const markAsRead = useCallback((analysisId) => {
    const updatedAnalyses = analyses.map(analysis => 
      analysis.id === analysisId 
        ? { ...analysis, isRead: true }
        : analysis
    );
    saveToStorage(updatedAnalyses);
  }, [analyses, saveToStorage]);

  // Удаляем анализ
  const deleteAnalysis = useCallback((analysisId) => {
    const updatedAnalyses = analyses.filter(analysis => analysis.id !== analysisId);
    saveToStorage(updatedAnalyses);
  }, [analyses, saveToStorage]);

  // Получаем количество непрочитанных анализов
  const unreadCount = analyses.filter(a => !a.isRead && a.status === 'completed').length;

  // Получаем количество pending анализов
  const pendingCount = analyses.filter(a => a.status === 'pending' || a.status === 'processing').length;

  // Умный polling - только если есть pending анализы
  useEffect(() => {
    if (pendingCount === 0) {
      return; // Нет pending анализов, polling не нужен
    }

    const interval = setInterval(() => {
      updatePendingAnalyses();
    }, 30000); // 30 секунд

    return () => clearInterval(interval);
  }, [pendingCount, updatePendingAnalyses]);

  return {
    analyses,
    unreadCount,
    pendingCount,
    isUpdating,
    addAnalysis,
    updatePendingAnalyses,
    markAsRead,
    deleteAnalysis
  };
};
